<template>
  <div
    class="flex items-center justify-between"
    style="height: 56px; padding-left: 16px; padding-right: 16px; gap: 16px;"
  >
    <div
      class="flex items-center"
      style="gap: 16px;"
    >
      <template v-if="!isDeploymentDetailPage">
        <img
          src="/countly.png"
          alt="Countly"
          class="cursor-pointer"
          style="height: 28px;"
          @click="onClickLogo"
        >
        <div
          style="width: 1px; height: 16px; background: rgba(255,255,255,0.2);"
        />
      </template>

      <!-- Breadcrumb mode: deployment detail page -->
      <template v-if="isDeploymentDetailPage">
        <div class="flex items-center">
          <div
            class="flex items-center cursor-pointer"
            @click="goToDashboard"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.6)"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </div>
          <span
            class="cursor-pointer ml-3"
            style="font-size: 12px; font-weight: 500; color: rgba(255,255,255,0.8);"
            @click="goToDashboard"
          >{{ populatedCompany.name }}</span>
          <svg
            class="mx-1.5"
            width="5"
            height="9"
            viewBox="0 0 7 12"
            fill="none"
          >
            <path
              d="M1 1l5 5-5 5"
              stroke="rgba(255,255,255,0.4)"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <cly-dropdown
            ref="serverDropdown"
            placement="bottom-start"
            class="cursor-pointer"
          >
            <template #trigger>
              <div class="flex items-center">
                <span style="font-size: 12px; font-weight: 500; color: #fff;">{{ currentDeploymentName }}</span>
                <svg
                  class="ml-1"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.6)"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </template>
            <div
              style="border-radius: 8px; overflow: hidden;"
              class="shadow-md bg-white"
            >
              <div class="p-2">
                <div
                  v-for="dep in deploymentsList"
                  :key="dep._id"
                  class="flex justify-between cursor-pointer items-center px-3 py-2 hover:bg-cool-gray-10"
                  style="border-radius: 6px;"
                  @click="goToDeployment(dep._id)"
                >
                  <div
                    class="text-cool-gray-100"
                    style="font-size: 12px;"
                    :class="[dep._id === $route.params.id && 'text-blue-100 font-medium']"
                  >
                    {{ dep.name }}
                  </div>
                  <div v-if="dep._id === $route.params.id">
                    <check-icon class="w-[10px] h-[7px] text-blue-100" />
                  </div>
                </div>
              </div>
            </div>
          </cly-dropdown>
        </div>
      </template>

      <!-- Default mode: workspace selector -->
      <template v-else>
        <div class="flex flex-col">
          <span style="font-size: 9px; font-weight: 400; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: 0.5px;">Workspace</span>
          <cly-dropdown
            ref="companyDropdown"
            placement="bottom-start"
            class="cursor-pointer"
          >
            <template #trigger>
              <div
                class="flex items-center"
                style="margin-top: 2px;"
              >
                <span style="font-size: 12px; font-weight: 500; color: #fff;">{{ populatedCompany.name }}</span>
                <svg
                  class="ml-1"
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.6)"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </template>
            <div
              style="border-radius: 8px; overflow: hidden;"
              class="shadow-md bg-white"
            >
              <div class="p-2">
                <div
                  v-for="(company, index) in sortedCompanies"
                  :key="index"
                  class="flex justify-between cursor-pointer items-center px-3 py-2 hover:bg-cool-gray-10"
                  style="border-radius: 6px;"
                  @click="setCompany(company._id)"
                >
                  <div
                    class="text-cool-gray-100"
                    style="font-size: 12px;"
                    :class="[company._id === $route.params.cid && 'text-blue-100 font-medium']"
                  >
                    {{ company.name }}
                  </div>
                  <div v-if="company._id === $route.params.cid">
                    <check-icon class="w-[10px] h-[7px] text-blue-100" />
                  </div>
                </div>
              </div>
            </div>
          </cly-dropdown>
        </div>
      </template>
    </div>

    <!-- Right side -->
    <div
      class="flex items-center"
      style="gap: 8px;"
    >
      <user-menu-dropdown />
    </div>
  </div>
</template>

<script>
import CheckIcon from '@/components/icons/check.vue';
import { swr, DEPLOYMENTS_FETCH_ALL_COUNT } from '@/modules/deployment/DeploymentService';
import {
  useCompanyStore,
  useProjectStore,
} from '@/stores';
import { computed, reactive } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import UserMenuDropdown from './user-menu-dropdown.vue';

export default {
  name: 'HeaderLoggedIn',
  components: {
    CheckIcon,
    UserMenuDropdown,
  },
  setup() {
    const projectStore = useProjectStore();
    const companyStore = useCompanyStore();

    const { selectedProject, projects } = storeToRefs(projectStore);
    const { selectedCompany, companies } = storeToRefs(companyStore);

    const reactiveRequest = reactive({
      company: computed(() => companyStore.selectedCompany),
      // without an explicit count the endpoint defaults to 3, which truncated
      // the server-switcher dropdown for workspaces with more servers
      count: DEPLOYMENTS_FETCH_ALL_COUNT,
    });
    const { data: deploymentsList } = swr.getByCompany(reactiveRequest);

    return {
      projects,
      companies,
      selectedCompany,
      selectedProject,
      deploymentsList,
    };
  },
  data() {
    return {
      selectedProjectLocal: '',
      selectedCompanyLocal: '',
      firstCompanySetFlag: true,
    };
  },
  computed: {
    populatedCompany() {
      if ((this.companies && this.companies.length) && this.selectedCompany !== '0') {
        return this.companies.find((c) => c._id === this.selectedCompany) || { name: '' };
      }
      return { name: '' };
    },
    projectOptions() {
      return (this.projects && this.projects[this.selectedCompanyLocal] && this.projects[this.selectedCompanyLocal].length) ? this.projects[this.selectedCompanyLocal] : [];
    },
    sortedCompanies() {
      const companiesArr = [...this.companies];
      return companiesArr.sort((a, b) => {
        if (a.name > b.name) return 1;
        if (a.name < b.name) return -1;
        return 0;
      });
    },
    isDeploymentDetailPage() {
      return this.$route.name === 'deploymentsDetail';
    },
    currentDeploymentName() {
      if (!this.deploymentsList || !this.$route.params.id) return '';
      const dep = this.deploymentsList.find((d) => d._id === this.$route.params.id);
      return dep?.name || '';
    },
  },
  watch: {
    selectedCompanyLocal(newCompanyValue) {
      if (this.firstCompanySetFlag) {
        this.firstCompanySetFlag = false;
        return;
      }
      // navigate to loading
      this.$router.push({
        name: 'Loading',
        query: {
          referrer: 'filter',
          pid: this.projectOptions[0]._id,
          cid: newCompanyValue,
        },
      });
    },
  },
  mounted() {
    this.selectedCompanyLocal = this.selectedCompany;
    this.selectedProjectLocal = this.selectedProject;
  },
  methods: {
    setCompany(companyId) {
      this.selectedCompanyLocal = companyId;
    },
    onClickLogo() {
      this.$router.push({
        name: 'dashboardRoot',
        params: this.$route.params,
      }).catch(() => {});
    },
    goToDashboard() {
      this.$router.push({
        name: 'dashboardRoot',
        params: this.$route.params,
      }).catch(() => {});
    },
    goToDeployment(deploymentId) {
      if (this.$refs.serverDropdown) {
        this.$refs.serverDropdown.doClose();
      }
      this.$router.push({
        name: 'deploymentsDetail',
        params: { ...this.$route.params, id: deploymentId },
      }).catch(() => {});
    },
  },
};
</script>
